// src/components/lesson/diagrams/Triangle.tsx
//
// GENERAL triangle (any angles — acute, obtuse, scalene, isosceles), unlike
// RightTriangle which is right-angle-only. Built from its three interior
// angles via the law of sines, then auto-fit + centered in the canvas. Covers
// the "Lines, Angles, and Triangles" figures the bank could not render:
// angle-sum, exterior angles, isosceles, similar-triangle setups.
//
// Construction: angles = [A, B, C] at vertices A, B, C (degrees, sum ~180; C is
// derived if the three don't sum to 180 so the picture is always closed). Base
// AB is unit length; AC = sinB/sinC. Vertices are then scaled to fill the box.
//
// Params (all optional):
//   angles?: [number, number, number]   // interior angles at A, B, C
//   vertexLabels?: [string, string, string]   // e.g. ["A","B","C"]
//   sideLabels?: [string, string, string]      // side OPPOSITE each vertex: [a,b,c]
//   angleLabels?: [string, string, string]     // override angle text, e.g. ["x°","","2x°"]
//   showAngleArcs?: boolean              // small arc at each vertex (default true)
//   label?: string

type Pt = { x: number; y: number }

type TriangleParams = {
  angles?: [number, number, number]
  vertexLabels?: [string, string, string]
  sideLabels?: [string, string, string]
  angleLabels?: [string, string, string]
  showAngleArcs?: boolean
  label?: string
}

const W = 340
const H = 260
const PAD = 42

const SHAPE = 'rgb(148 163 184)'
const FILL = 'rgb(34 211 238 / 0.10)'
const SIDE = 'rgb(34 211 238)'
const ANGLE = 'rgb(251 191 36)'

const rad = (d: number) => (d * Math.PI) / 180

/** Map math-space vertices (y up) into the canvas, auto-fit + centered. */
function fit(verts: Pt[]): Pt[] {
  const xs = verts.map((v) => v.x)
  const ys = verts.map((v) => v.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const w = Math.max(maxX - minX, 1e-6)
  const h = Math.max(maxY - minY, 1e-6)
  const scale = Math.min((W - 2 * PAD) / w, (H - 2 * PAD) / h)
  const drawnW = w * scale
  const drawnH = h * scale
  const offX = (W - drawnW) / 2
  const offY = (H - drawnH) / 2
  // Flip y: larger math-y → higher on screen (smaller screen-y).
  return verts.map((v) => ({
    x: offX + (v.x - minX) * scale,
    y: offY + (maxY - v.y) * scale,
  }))
}

/** Small interior-angle arc at vertex v, swept the SHORT way between the two
 *  neighbor directions (screen space). Returns a polyline points string. */
function angleArc(v: Pt, n1: Pt, n2: Pt, r = 16): string {
  const a1 = Math.atan2(n1.y - v.y, n1.x - v.x)
  const a2 = Math.atan2(n2.y - v.y, n2.x - v.x)
  let diff = a2 - a1
  while (diff > Math.PI) diff -= 2 * Math.PI
  while (diff < -Math.PI) diff += 2 * Math.PI
  const steps = 14
  const out: string[] = []
  for (let i = 0; i <= steps; i++) {
    const a = a1 + (diff * i) / steps
    out.push(`${v.x + r * Math.cos(a)},${v.y + r * Math.sin(a)}`)
  }
  return out.join(' ')
}

export default function Triangle({ params }: { params: Record<string, unknown> }) {
  const p = params as TriangleParams
  const raw = Array.isArray(p.angles) ? p.angles : []
  let A = typeof raw[0] === 'number' && raw[0] > 0 ? raw[0] : 50
  let B = typeof raw[1] === 'number' && raw[1] > 0 ? raw[1] : 60
  // Derive C from A,B so the triangle always closes, even if the caller's three
  // angles don't sum to 180. Clamp A+B < 180.
  if (A + B >= 179) {
    const s = 178 / (A + B)
    A *= s
    B *= s
  }
  const C = 180 - A - B

  // Law of sines with base AB = 1: AC = sinB / sinC. A at origin, ray at angle A.
  const ac = Math.sin(rad(B)) / Math.sin(rad(C))
  const mathVerts: Pt[] = [
    { x: 0, y: 0 }, // A
    { x: 1, y: 0 }, // B
    { x: ac * Math.cos(rad(A)), y: ac * Math.sin(rad(A)) }, // C
  ]
  const [a, b, c] = fit(mathVerts)
  const centroid = { x: (a.x + b.x + c.x) / 3, y: (a.y + b.y + c.y) / 3 }
  const verts = [a, b, c]

  const vLabels = Array.isArray(p.vertexLabels) ? p.vertexLabels : []
  const sLabels = Array.isArray(p.sideLabels) ? p.sideLabels : []
  const aLabels = Array.isArray(p.angleLabels) ? p.angleLabels : []
  const showArcs = p.showAngleArcs !== false

  // Pull a label a little toward the centroid (for angles) or away (for vertices).
  const toward = (pt: Pt, amt: number) => ({
    x: pt.x + ((centroid.x - pt.x) * amt) / dist(pt, centroid),
    y: pt.y + ((centroid.y - pt.y) * amt) / dist(pt, centroid),
  })
  const away = (pt: Pt, amt: number) => toward(pt, -amt)

  // Side i is OPPOSITE vertex i: side a (idx0) is BC, side b (idx1) is CA,
  // side c (idx2) is AB. Midpoint of the opposite edge, pushed outward.
  const opp: [Pt, Pt][] = [
    [b, c],
    [c, a],
    [a, b],
  ]

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Triangle'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      <polygon
        points={verts.map((v) => `${v.x},${v.y}`).join(' ')}
        fill={FILL}
        stroke={SHAPE}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Angle arcs */}
      {showArcs &&
        verts.map((v, i) => {
          const others = verts.filter((_, j) => j !== i)
          return (
            <polyline
              key={`arc-${i}`}
              points={angleArc(v, others[0], others[1])}
              fill="none"
              stroke={ANGLE}
              strokeWidth="1.25"
            />
          )
        })}

      {/* Angle labels (near each vertex, nudged toward the centroid) */}
      {verts.map((v, i) => {
        const text = aLabels[i]
        if (!text) return null
        const lp = toward(v, 30)
        return (
          <text
            key={`ang-${i}`}
            x={lp.x}
            y={lp.y}
            fontSize="12"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={ANGLE}
          >
            {text}
          </text>
        )
      })}

      {/* Side labels (midpoint of the opposite edge, pushed outward) */}
      {opp.map(([p1, p2], i) => {
        const text = sLabels[i]
        if (!text) return null
        const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }
        const lp = away(mid, 16)
        return (
          <text
            key={`side-${i}`}
            x={lp.x}
            y={lp.y}
            fontSize="12"
            fontWeight="600"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={SIDE}
          >
            {text}
          </text>
        )
      })}

      {/* Vertex labels (just outside each vertex) */}
      {verts.map((v, i) => {
        const text = vLabels[i]
        if (!text) return null
        const lp = away(v, 14)
        return (
          <text
            key={`vtx-${i}`}
            x={lp.x}
            y={lp.y}
            fontSize="12"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={SHAPE}
          >
            {text}
          </text>
        )
      })}
    </svg>
  )
}

function dist(a: Pt, b: Pt): number {
  return Math.max(Math.hypot(a.x - b.x, a.y - b.y), 1e-6)
}
